import React, { useState, useEffect, useRef } from 'react';
import { Toast, Input, Dialog, Button } from 'antd-mobile';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { EditOutlined, BulbOutlined, CheckOutlined } from '@ant-design/icons';
import {t} from "i18next";

// 扩展MediaTrackCapabilities类型以支持手电筒
interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
  torch?: boolean;
}

// 扩展MediaTrackConstraintSet类型以支持手电筒
interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
  torch?: boolean;
}

export interface ScanComponentProps {
  onScanSuccess: (code: string) => void;
  onScanError?: (error: string) => void;
}

// 检查浏览器是否支持相机功能
const checkCameraSupport = (): boolean => {
  const isSupported = 'mediaDevices' in navigator && 
                     'getUserMedia' in navigator.mediaDevices;
  if (!isSupported) {
    Toast.show({
      icon: 'fail',
      content: '您的浏览器不支持相机功能',
      duration: 2000,
    });
    return false;
  }
  return true;
};

// 检查设备是否支持手电筒
const checkTorchSupport = (videoTrack: MediaStreamTrack): boolean => {
  if (!('getCapabilities' in videoTrack)) {
    return false;
  }
  const capabilities = videoTrack.getCapabilities() as ExtendedMediaTrackCapabilities;
  return !!capabilities.torch;
};

export const QrCodeScanComponent: React.FC<ScanComponentProps> = ({
  onScanSuccess,
  onScanError,
}) => {
  const [scanning, setScanning] = useState<boolean>(true);
  const [torchOn, setTorchOn] = useState<boolean>(false);
  const [manualInputVisible, setManualInputVisible] = useState<boolean>(false);
  const [isTorchSupported, setIsTorchSupported] = useState<boolean>(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerRef = useRef<HTMLDivElement>(null);
  const videoTrackRef = useRef<MediaStreamTrack | null>(null);

  // 处理手动输入对话框的显示/隐藏
  const handleManualInputVisible = (visible: boolean) => {
    setManualInputVisible(visible);
    // 当打开手动输入时，暂停扫描
    if (visible) {
      setScanning(false);
    } else {
      // 当关闭手动输入时，恢复扫描
      setScanning(true);
    }
  };

  useEffect(() => {
    if (scanning && scannerContainerRef.current) {
      if (!checkCameraSupport()) {
        return;
      }

      const id = 'barcode-scanner';
      const scannerContainer = scannerContainerRef.current;
      
      // 确保容器是空的
      scannerContainer.innerHTML = `<div id="${id}"></div>`;
      
      const scanner = new Html5Qrcode(id);
      scannerRef.current = scanner;

      const fps = 10;
      const qrboxWidth = window.innerWidth*0.8;
      const qrboxHeight = window.innerHeight*0.3;
      const formatsToSupport = [
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.QR_CODE,
      ];

      const config = {
        fps,
        qrbox: (viewfinderWidth: number, viewfinderHeight: number) => {
          const width = Math.max(viewfinderWidth * 0.9, qrboxWidth);
          const height = Math.max(viewfinderHeight * 0.8, qrboxHeight);
          return { width, height };
        },
        formatsToSupport
      };

      scanner.start(
        { facingMode: 'environment' },
        config,
        (decodedText) => {
          onScanSuccess(decodedText);
        },
        (errorMessage) => {
          if (onScanError) {
            onScanError(errorMessage);
          }
        }
      ).catch(err => {
        console.error('Failed to start scanner:', err);
        Toast.show({
          icon: 'fail',
          content: 'Unable to start camera',
        });
      });

      // 获取视频流并检查手电筒支持
      const video = document.getElementById(id)?.getElementsByTagName('video')[0];
      if (video && video.srcObject instanceof MediaStream) {
        const videoTracks = video.srcObject.getVideoTracks();
        if (videoTracks.length > 0) {
          videoTrackRef.current = videoTracks[0];
          setIsTorchSupported(checkTorchSupport(videoTracks[0]));
        }
      }
    }

    return () => {
      if (scannerRef.current) {
        if (scannerRef.current.isScanning) {
          scannerRef.current.stop().catch(err => {
            console.error('Failed to stop scanner:', err);
          });
        }
        scannerRef.current = null;
      }
      if (videoTrackRef.current) {
        videoTrackRef.current.stop();
        videoTrackRef.current = null;
      }
      if (scannerContainerRef.current) {
        scannerContainerRef.current.innerHTML = '';
      }
    };
  }, [scanning, onScanSuccess, onScanError]);

  // 切换手电筒
  const toggleTorch = async () => {
    try {
      if (!videoTrackRef.current || !isTorchSupported) {
        Toast.show({
          icon: 'fail',
          content: 'Your device does not support flashlights',
          duration: 2000,
        });
        return;
      }

      const newTorchState = !torchOn;
      
      await videoTrackRef.current.applyConstraints({
        advanced: [{ torch: newTorchState } as ExtendedMediaTrackConstraintSet]
      });
      
      setTorchOn(newTorchState);
      Toast.show({
        icon: 'success',
        content: newTorchState ? 'Flashlight turned on' : 'Flashlight turned off',
        duration: 1000,
      });
    } catch (error) {
      console.error('Flashlight control failed:', error);
      Toast.show({
        icon: 'fail',
        content: 'Flashlight function not available',
        duration: 2000,
      });
    }
  };

  return (
    <div className='flex flex-col flex-1'>
      <div className={`relative flex-1`}>
        <div ref={scannerContainerRef} className="w-full h-full"></div>
        {/* 操作按钮区域 */}
        <div className="absolute bottom-2 right-4 flex flex gap-4">
          <div
            onClick={() => {
              console.log('QrCode EditOutlined clicked!');
              handleManualInputVisible(true);
            }}   
            className='flex items-center justify-center w-8 h-8 rounded-full bg-white cursor-pointer'
            style={{ zIndex: 1000 }}
          >
            <EditOutlined />
          </div>
          {isTorchSupported && (
            <div
              onClick={toggleTorch}
              className='flex items-center justify-center w-8 h-8 rounded-full bg-white'
            >
              <BulbOutlined />
            </div>
          )}
        </div>
      </div>
      <ManualInputArea
        onScanSuccess={onScanSuccess}
        visible={manualInputVisible}
        setManualInputVisible={handleManualInputVisible} />
    </div>
  );
};

export const ManualInputArea = ({ onScanSuccess, visible, setManualInputVisible }: { onScanSuccess: (code: string) => void, visible: boolean, setManualInputVisible: (visible: boolean) => void }) => {
  const [manualInputValue, setManualInputValue] = useState<string>('');
  const handleConfirmSearch = () => {
    // 统一转换为大写，兼容大小写输入
    onScanSuccess(manualInputValue.trim().toUpperCase());
    setManualInputVisible(false);
    setManualInputValue('');
  }

  return (
    <Dialog
      visible={visible}
      destroyOnClose={true}
      onClose={() => setManualInputVisible(false)}
      closeOnMaskClick
      content={
          <div className='flex'>
            <Input
              placeholder={t('pages.scanInputBox') || ''}
              value={manualInputValue}
              onChange={val => setManualInputValue(val)}
              onEnterPress={handleConfirmSearch}
              autoFocus
            />
            <Button
              color='primary'
              onClick={handleConfirmSearch}
              style={{
                border: 'none'
              }}
            >
              <CheckOutlined style={{ color: 'white', fontSize: '18px' }} />
            </Button>
          </div>
      }
    />
  );
};
